import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import { useSelector } from "react-redux";
import { selectEvents } from "../../features/eventSlice";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import { Tooltip } from "@mui/material";
import { EmptyState } from "../UI/States";

var events = [];

// a custom render function
function renderEventContent(eventInfo) {
  return (
    <button
      type="button"
      className="flex w-full min-w-0 items-baseline gap-1 overflow-hidden rounded-md border border-sky-500/30 bg-sky-500/15 px-1.5 py-1 text-left text-xs text-sky-900 transition-colors hover:bg-sky-500/25 dark:text-sky-100"
    >
      <span className="shrink-0 font-semibold tabular-nums">
        {eventInfo.timeText}
      </span>
      <span className="truncate">{eventInfo.event.title}</span>
    </button>
  );
}

const MyCalendar = () => {
  const event = useSelector(selectEvents);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // The Outlook calendar loads in the background after sign-in (NavListAuth),
  // so it may not be here yet when the dialog opens.
  const eventList = event?.value || [];

  const getEvents = () => {
    events = [];
    eventList.map((item, index) => {
      events.push({
        title: item.subject,
        start: new Date(item.start.dateTime) - 14400000,
        end: new Date(item.end.dateTime) - 14400000,
        onlineMeetingUrl: item?.onlineMeeting?.joinUrl, // Add the onlineMeeting.joinUrl property
        attendees: item.attendees,
      });
    });

    return events;
  };

  const handleEventClick = (clickInfo) => {
    setSelectedEvent(clickInfo.event);
  };

  const handlePopupClose = () => {
    setSelectedEvent(null);
  };

  useEffect(() => {
    setSelectedEvent(null);
  }, [event?.value]);

  return (
    <div className="relative flex h-[calc(100vh-10rem)] min-h-[28rem] w-full flex-col px-4 pb-4 sm:px-6 sm:pb-6">
      {eventList.length > 0 ? (
        <FullCalendar
          height={"100%"}
          width={100}
          plugins={[dayGridPlugin]}
          initialView="dayGridMonth"
          weekends={false}
          events={getEvents()}
          eventContent={renderEventContent}
          eventTimeFormat={{
            hour: "numeric",
            minute: "2-digit",
            meridiem: "Short",
          }}
          eventClick={handleEventClick}
          eventMouseEnter={(mouseEnterInfo) => {
            mouseEnterInfo.el.setAttribute("title", "Click for more info");
          }}
        />
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <EmptyState
            icon={EventBusyRoundedIcon}
            title="No events to show"
            description="Events from your Outlook calendar show up here once they've loaded."
          />
        </div>
      )}
      {selectedEvent && (
        <div
          role="dialog"
          aria-label={selectedEvent.title}
          className="app-glass app-glass-rim absolute left-1/2 top-1/2 z-40 flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col rounded-3xl border p-6"
        >
          <button
            type="button"
            onClick={handlePopupClose}
            aria-label="Close event details"
            className="app-icon-btn absolute right-3 top-3 h-9 w-9"
          >
            <CloseRoundedIcon fontSize="small" />
          </button>

          <p className="app-eyebrow">
            {selectedEvent.start.toLocaleDateString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </p>
          <h2 className="mt-1 pr-8 text-xl font-bold tracking-tight">
            {selectedEvent.title}
          </h2>
          <p className="app-muted mt-1 text-sm tabular-nums">
            {selectedEvent.start.toLocaleTimeString()} -{" "}
            {selectedEvent.end.toLocaleTimeString()}
          </p>

          <p className="app-eyebrow mt-5">Attendees</p>
          <div className="mt-2 flex max-h-36 flex-wrap gap-1.5 overflow-auto">
            {selectedEvent.extendedProps.attendees.map((item, index) => {
              return (
                <Tooltip key={index} title={item.emailAddress.address}>
                  <span className="app-chip">{item.emailAddress.name}</span>
                </Tooltip>
              );
            })}
          </div>
          {selectedEvent.extendedProps.onlineMeetingUrl && (
            <div className="app-divider mt-5 flex justify-end border-t pt-4">
              <a
                href={selectedEvent.extendedProps.onlineMeetingUrl}
                target="_blank"
                rel="noreferrer"
                className="app-btn app-btn-primary"
              >
                <VideocamRoundedIcon sx={{ fontSize: 18 }} />
                Join Meeting
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyCalendar;
